/**
 * AICoachButton - Button to open the AI Coach modal
 *
 * Placed in the Training Calendar header to provide easy access
 * to AI-powered training plan adjustments.
 *
 * Only visible when AI Engine mode is enabled in profile settings.
 */

import { AutoAwesome as AIIcon } from '@mui/icons-material';
import { Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { loadUserProfileSync } from '../../utils/userProfile';
import AICoachModal from './AICoachModal';

interface AICoachButtonProps {
  /** Callback when changes are applied to the training plan */
  onChangesApplied: () => void;
  /** Optional: Disable the button */
  disabled?: boolean;
  /** Optional: Make button full width */
  fullWidth?: boolean;
}

const AICoachButton: React.FC<AICoachButtonProps> = ({
  onChangesApplied,
  disabled = false,
  fullWidth = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalOpen, setModalOpen] = useState(false);
  const [aiEngineEnabled, setAiEngineEnabled] = useState(false);

  // Load profile and listen for profile updates
  useEffect(() => {
    const loadAiSetting = () => {
      const profile = loadUserProfileSync();
      setAiEngineEnabled(profile?.aiEngineEnabled === true);
    };

    // Load initially
    loadAiSetting();

    // Listen for profile updates
    const handleProfileUpdate = () => {
      loadAiSetting();
    };

    window.addEventListener('user-profile-updated', handleProfileUpdate);
    window.addEventListener('storage', handleProfileUpdate);

    return () => {
      window.removeEventListener('user-profile-updated', handleProfileUpdate);
      window.removeEventListener('storage', handleProfileUpdate);
    };
  }, []);

  // Don't render anything if AI Engine is not enabled
  if (!aiEngineEnabled) {
    return null;
  }

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Tooltip title="Ask AI Coach to adjust your training plan">
        <span>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenModal}
            disabled={disabled}
            fullWidth={fullWidth}
            startIcon={<AIIcon />}
            aria-label="Open AI Training Coach"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              minWidth: fullWidth ? 'auto' : isMobile ? 'auto' : 200,
              flex: fullWidth ? 'none' : isMobile ? 'none' : 1,
              maxWidth: fullWidth ? 'none' : isMobile ? 'auto' : 220,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              // Gradient border effect for AI feature
              borderColor: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'primary.50',
              },
            }}
          >
            {isMobile ? 'AI Coach' : 'Ask AI Coach'}
          </Button>
        </span>
      </Tooltip>

      <AICoachModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onChangesApplied={onChangesApplied}
      />
    </>
  );
};

export default AICoachButton;
